import { appEnv } from '@/lib/env';
import { useTheme } from '@/context/ThemeContext';
import { useSiteContent } from '@/context/SiteContentContext';

interface Props {
  title: string;
  message: string;
}

export default function MaintenancePage({ title, message }: Props) {
  const { theme } = useTheme();
  const { content } = useSiteContent();
  const light = theme === 'light';
  const logoFilter = light ? 'brightness-0' : 'brightness-0 invert';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${light ? 'bg-gray-50 text-gray-900' : 'bg-[#07101C] text-white'}`}>
      <div className="relative min-h-screen overflow-hidden px-6 py-12">
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background: light
              ? 'radial-gradient(circle at top right, rgba(14,165,233,0.18), transparent 35%), radial-gradient(circle at bottom left, rgba(2,132,199,0.12), transparent 30%)'
              : 'radial-gradient(circle at top right, rgba(14,165,233,0.24), transparent 32%), radial-gradient(circle at bottom left, rgba(249,115,22,0.16), transparent 28%)',
          }}
        ></div>

        <div className="relative z-10 mx-auto flex min-h-[calc(100vh-6rem)] max-w-4xl items-center justify-center">
          <div className={`w-full max-w-2xl rounded-[28px] border px-8 py-12 text-center shadow-2xl md:px-14 ${light ? 'border-gray-200 bg-white/90' : 'border-white/10 bg-white/5 backdrop-blur-xl'}`}>
            <div className="mb-8">
              <img
                src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
                alt={`${appEnv.appName} logo`}
                className={`mx-auto mb-6 h-10 w-auto object-contain ${logoFilter}`}
              />
              <span
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em]"
                style={{
                  color: 'var(--accent)',
                  backgroundColor: 'color-mix(in srgb, var(--accent) 14%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent) 32%, transparent)',
                }}
              >
                <i className="ri-tools-line"></i>
                Maintenance Mode
              </span>
            </div>

            <h1 className={`mb-4 text-4xl font-black leading-tight md:text-5xl ${light ? 'text-gray-900' : 'text-white'}`}>
              {title}
            </h1>
            <p className={`mx-auto mb-4 max-w-xl text-base leading-relaxed md:text-lg ${light ? 'text-gray-600' : 'text-white/70'}`}>
              {message}
            </p>
            <p className={`mx-auto max-w-lg text-sm leading-relaxed ${light ? 'text-gray-500' : 'text-white/45'}`}>
              Thank you for your patience. If you need immediate assistance, please contact{' '}
              <a
                href={`mailto:${content.footer.email}`}
                className="font-semibold"
                style={{ color: 'var(--accent)' }}
              >
                {content.footer.email}
              </a>
              .
            </p>

            <div className={`mx-auto mt-10 grid max-w-xl grid-cols-1 gap-3 rounded-2xl border p-4 text-left sm:grid-cols-3 ${light ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-[#0D1B2A]/70'}`}>
              {[
                { icon: 'ri-timer-line', label: 'Status', value: 'Work in progress' },
                { icon: 'ri-flashlight-line', label: 'Goal', value: 'A cleaner relaunch' },
                { icon: 'ri-ship-line', label: 'ETA', value: 'Back shortly' },
              ].map((item) => (
                <div key={item.label} className="rounded-xl px-3 py-3">
                  <div
                    className="mb-3 flex h-9 w-9 items-center justify-center rounded-full"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 18%, transparent)' }}
                  >
                    <i className={item.icon} style={{ color: 'var(--accent)' }}></i>
                  </div>
                  <p className={`text-[11px] font-bold uppercase tracking-widest ${light ? 'text-gray-500' : 'text-white/45'}`}>
                    {item.label}
                  </p>
                  <p className={`mt-1 text-sm font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
